import { cva, VariantProps } from 'class-variance-authority';
import mergeRefs from 'merge-refs';
import * as React from 'react';
import { useRef } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { hideNumberInputSpinnersClassName } from 'v5/platform/dom/hideNumberInputSpinnersClassName';
import { focusRings } from './_common/focusRing';

/**
 * @deprecated Use {@link input2Variants} from `./Input2` instead.
 * Map `variant` (`regular` | `inline` | `plain`) to `tone`; use `variant` for semantic color (`default` | `danger`).
 */
export const inputVariants = cva(
  [
    'outline-none overflow-auto',
    'flex h-10 w-full py-2',
    'file:border-0 file:bg-transparent file:text-base file:font-medium file:text-fg-default',
    'placeholder:text-fg-muted',
    'disabled:cursor-not-allowed aria-readonly:cursor-default disabled:opacity-50'
  ],
  {
    variants: {
      size: {
        xs: 'text-base h-[20px] py-1',
        sm: 'text-base h-9 py-1.5',
        md: 'text-base h-12 py-2',
        lg: 'text-lg h-15 py-3 font-semibold',
        xl: 'text-xl h-17 py-4 font-semibold'
      },
      variant: {
        regular: [
          'rounded',
          'border border-border-default bg-bg-default text-fg-default',
          'px-3',
          'not-aria-readonly:hocus:border-bg-inset',
          focusRings.focus.normal,
          focusRings.focus.invalidFocused
        ],
        inline: [
          'transition-all',
          'bg-transparent text-fg-default',
          'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-border-default',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus:shadow-brand-400'
        ],
        plain: [
          'transition-all',
          'bg-transparent text-fg-default',
          'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-transparent',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus:shadow-brand-400'
        ]
      }
    },
    defaultVariants: {
      variant: 'regular',
      size: 'md'
    },
    compoundVariants: [
      {
        variant: 'regular',
        size: 'sm',
        className: 'rounded'
      }
    ]
  }
);

/**
 * @deprecated Use {@link textArea2Variants} from `./Input2` instead.
 */
export const textAreaVariants = cva(
  [
    'outline-none overflow-hidden max-h-80',
    'flex h-10 w-full py-2',
    'file:border-0 file:bg-transparent file:text-base file:font-medium file:text-fg-default',
    'placeholder:text-fg-muted',
    'disabled:cursor-not-allowed aria-readonly:cursor-default disabled:opacity-50'
  ],
  {
    variants: {
      size: {
        xs: 'text-base h-[20px] min-h-[20px] py-1 leading-[8px]',
        sm: 'text-base h-9 min-h-9 py-1.5 leading-[17px]',
        md: 'text-base h-12 min-h-12 py-2 leading-[23px]',
        lg: 'text-lg h-15 min-h-15 py-3 font-semibold leading-[25px]',
        xl: 'text-xl h-17 min-h-17 py-4 font-semibold leading-[23px]'
      },
      variant: {
        regular: [
          'rounded',
          'border border-border-default bg-bg-default text-fg-default',
          'px-3',
          'not-aria-readonly:hocus:border-bg-inset',
          focusRings.focus.normal,
          focusRings.focus.invalidFocused
        ],
        inline: [
          'transition-all',
          'bg-transparent text-fg-default',
          'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-border-default',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus:shadow-brand-400'
        ],
        plain: [
          'transition-all',
          'bg-transparent text-fg-default',
          'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-transparent',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus:shadow-brand-400'
        ]
      }
    },
    defaultVariants: {
      variant: 'regular',
      size: 'md'
    },
    compoundVariants: [
      {
        variant: 'regular',
        size: 'sm',
        className: 'rounded'
      }
    ]
  }
);

/**
 * @deprecated Use {@link Input2Props} from `./Input2` instead.
 */
export type InputProps = Omit<
  React.ComponentProps<'input'>,
  'size' | 'readOnly'
> &
  VariantProps<typeof inputVariants> & {
    highlightOnFocus?: boolean;
    readOnly?: boolean;
  };

export type InputSize = InputProps['size'];

/** @deprecated Use {@link Input2Tone} from `./Input2` instead (`regular` | `inline` | `plain`). */
export type InputVariant = InputProps['variant'];

/**
 * @deprecated Use {@link Input2} from `./Input2` instead.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      highlightOnFocus,
      onFocus,
      variant,
      size,
      readOnly,
      ...props
    },
    ref
  ) => {
    return (
      <input
        type={type}
        className={cn(
          inputVariants({ variant, size }),
          type === 'number' && hideNumberInputSpinnersClassName,
          className
        )}
        ref={ref}
        tabIndex={readOnly ? -1 : props.tabIndex}
        aria-readonly={readOnly ? 'true' : undefined}
        readOnly={readOnly}
        {...props}
        onFocus={e => {
          if (highlightOnFocus && !readOnly) {
            e.target.select();
          }
          onFocus?.(e);
        }}
      />
    );
  }
);
Input.displayName = 'Input';

/**
 * @deprecated Use {@link TextArea2Props} from `./Input2` instead.
 */
export type TextAreaProps = Omit<
  React.ComponentProps<'textarea'>,
  'size' | 'readOnly'
> &
  VariantProps<typeof inputVariants> & {
    highlightOnFocus?: boolean;
    readOnly?: boolean;
    /** Grow height to fit content (default). Set false when the parent sets height (e.g. grid cell popover). */
    autoResize?: boolean;
  };

/**
 * @deprecated Use {@link TextArea2} from `./Input2` instead.
 */
const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      highlightOnFocus,
      onFocus,
      variant,
      size,
      readOnly,
      autoResize = true,
      rows = 1,
      ...props
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || !autoResize) {
        return;
      }
      textarea.style.minHeight = 'auto';
      textarea.style.minHeight = `${textarea.scrollHeight}px`;
    }, [autoResize]);

    React.useEffect(() => {
      adjustHeight();
    }, [adjustHeight, props.value]);

    return (
      <textarea
        ref={mergeRefs(ref, textareaRef)}
        className={cn(
          textAreaVariants({ variant, size }),
          readOnly && 'resize-none',
          !autoResize && 'resize-none',
          className
        )}
        aria-readonly={readOnly ? 'true' : undefined}
        readOnly={readOnly}
        rows={rows}
        {...props}
        onFocus={e => {
          if (highlightOnFocus && !readOnly) {
            e.target.select();
          }
          onFocus?.(e);
        }}
        onChange={e => {
          adjustHeight();
          props.onChange?.(e);
        }}
      />
    );
  }
);
TextArea.displayName = 'TextArea';

export { Input, TextArea };
